/**
 * AdminAiUsageScreen — observabilidad de las generaciones con IA (Fase 4).
 *
 * Layout:
 *   ┌───────────────────────────────────────────────────────┐
 *   │ KPIs del mes (gens / cost / tokens / coaches únicos)  │
 *   ├──────────────────┬────────────────────────────────────┤
 *   │ Status breakdown │ Top 10 coachs del mes              │
 *   ├──────────────────┴────────────────────────────────────┤
 *   │ Tabla paginada de generaciones (filtros + CSV export) │
 *   └───────────────────────────────────────────────────────┘
 *
 * Datos: `GET /aetherlite/admin/ai-usage/summary` (KPIs) +
 * `GET /aetherlite/admin/ai-usage/list?limit&offset&status&user_id` (tabla).
 * CSV: `GET /aetherlite/admin/export/ai-usage.csv`.
 *
 * Sin auto-refresh — el admin recarga manualmente. La tabla pagina con
 * limit=50 por página. Filtros por status (success/error/timeout/malformed)
 * y por user_id (chip cliqueable desde "Top coachs"). Costos en USD con 4
 * decimales (el rango típico es 0.02-0.05 USD por generación).
 */
import { useCallback, useEffect, useState } from "react";
import { useToast } from "../../lib/toast";
import { Icon } from "../../lib/Icon";
import {
  adminAiUsageList,
  adminAiUsageSummary,
  adminGetAiConfig,
  adminSetAiConfig,
  downloadBlob,
  exportCsv,
  type AiUsageRow,
  type AiUsageSummary,
} from "../../api/admin";
import { AdminBadge, AdminGate, formatDateTime, relativeTime, Skeleton, tierLabel } from "./_shared";

const PAGE_SIZE = 50;

export function AdminAiUsageScreen() {
  return (
    <AdminGate>
      <Inner />
    </AdminGate>
  );
}

function Inner() {
  const toast = useToast();
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [summaryErr, setSummaryErr] = useState<string | null>(null);
  const [rows, setRows] = useState<AiUsageRow[] | null>(null);
  const [rowsErr, setRowsErr] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterUser, setFilterUser] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [csvBusy, setCsvBusy] = useState(false);

  const loadSummary = useCallback(() => {
    setSummaryErr(null);
    adminAiUsageSummary()
      .then(setSummary)
      .catch((e) => setSummaryErr(e instanceof Error ? e.message : String(e)));
  }, []);

  const loadRows = useCallback(() => {
    setRowsErr(null);
    adminAiUsageList({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      status: filterStatus || undefined,
      user_id: filterUser ?? undefined,
    })
      .then(setRows)
      .catch((e) => setRowsErr(e instanceof Error ? e.message : String(e)));
  }, [page, filterStatus, filterUser]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const handleExport = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    try {
      const blob = await exportCsv("ai-usage");
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `aetherlite-ai-usage-${stamp}.csv`);
      toast("CSV de uso IA descargado");
    } catch (e) {
      toast("No pude exportar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy, toast]);

  const clearFilters = () => {
    setFilterStatus("");
    setFilterUser(null);
    setPage(0);
  };

  return (
    <div className="page al-admin-shell">
      <div className="al-admin-head">
        <div>
          <h1 className="al-page-title">
            Uso de IA <AdminBadge />
          </h1>
          <p className="muted">
            Generaciones con Claude Haiku — costo, tokens y errores.
            {summary && (
              <> Mes <b>{summary.month_label}</b>.</>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { loadSummary(); loadRows(); }}>
            <Icon name="swap" size={14} /> Refrescar
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleExport} disabled={csvBusy}>
            <Icon name="upload" size={14} /> {csvBusy ? "Exportando…" : "Exportar CSV"}
          </button>
        </div>
      </div>

      {/* ---------- Config: límite por cliente/mes ---------- */}
      <AiConfigCard />

      {/* ---------- KPIs del mes ---------- */}
      {summaryErr && (
        <div className="card" style={{ padding: 16, color: "var(--danger)" }}>
          Error cargando KPIs: {summaryErr}
        </div>
      )}
      {!summary && !summaryErr && (
        <div className="al-admin-mini-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="al-admin-mini"><Skeleton height={28} /></div>
          ))}
        </div>
      )}
      {summary && <KpisGrid s={summary} />}

      {/* ---------- Status breakdown + Top coachs ---------- */}
      {summary && (
        <div className="al-admin-ai-twocol">
          <StatusBreakdownCard s={summary} />
          <TopCoachesCard s={summary} onPickUser={(uid) => { setFilterUser(uid); setPage(0); }} />
        </div>
      )}

      {/* ---------- Tabla paginada ---------- */}
      <div className="card al-admin-card">
        <div className="card-header">
          <div className="card-title">
            <Icon name="book" size={14} /> Generaciones recientes
          </div>
          <div className="al-admin-ai-filters">
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            >
              <option value="">Todos los status</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="timeout">Timeout</option>
              <option value="malformed">Malformed</option>
            </select>
            {filterUser !== null && (
              <button
                className="chip"
                onClick={() => { setFilterUser(null); setPage(0); }}
                title="Quitar filtro de coach"
              >
                user_id={filterUser} <Icon name="x" size={10} />
              </button>
            )}
            {(filterStatus || filterUser !== null) && (
              <button className="link-btn tiny" onClick={clearFilters}>
                Limpiar
              </button>
            )}
          </div>
        </div>

        {rowsErr && (
          <div style={{ padding: 16, color: "var(--danger)" }}>
            Error: {rowsErr}
          </div>
        )}
        {!rows && !rowsErr && (
          <div style={{ padding: 16 }}>
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={32} />)}
          </div>
        )}
        {rows && <GenerationsTable rows={rows} onPickUser={(uid) => { setFilterUser(uid); setPage(0); }} />}

        {/* Paginación */}
        <div className="al-admin-ai-pager">
          <button
            className="btn btn-ghost btn-sm"
            disabled={page === 0}
            onClick={() => setPage(Math.max(0, page - 1))}
          >
            <Icon name="arrowL" size={12} /> Anterior
          </button>
          <span className="tiny muted">
            Página {page + 1} · mostrando {rows?.length ?? 0} resultados
          </span>
          <button
            className="btn btn-ghost btn-sm"
            disabled={!rows || rows.length < PAGE_SIZE}
            onClick={() => setPage(page + 1)}
          >
            Siguiente <Icon name="arrow" size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function KpisGrid({ s }: { s: AiUsageSummary }) {
  return (
    <div className="al-admin-mini-grid">
      <Mini label="Generaciones del mes" value={String(s.total_generations)} />
      <Mini label="Exitosas" value={String(s.successful_generations)} />
      <Mini label="Costo total" value={`$${s.total_cost_usd.toFixed(4)}`} />
      <Mini label="Costo / generación" value={`$${s.avg_cost_per_success_usd.toFixed(4)}`} />
      <Mini label="Coachs únicos" value={String(s.unique_coaches)} />
      <Mini
        label="Tokens (in/out)"
        value={`${formatCompact(s.total_tokens_in)} / ${formatCompact(s.total_tokens_out)}`}
      />
    </div>
  );
}

function StatusBreakdownCard({ s }: { s: AiUsageSummary }) {
  const total = s.total_generations || 1;
  const items: Array<[string, number, string]> = [
    ["Success", s.status_breakdown.success, "#059669"],
    ["Error", s.status_breakdown.error, "#dc2626"],
    ["Timeout", s.status_breakdown.timeout, "#f59e0b"],
    ["Malformed", s.status_breakdown.malformed, "#7c3aed"],
  ];
  return (
    <div className="card al-admin-card">
      <div className="card-header">
        <div className="card-title">
          <Icon name="grid" size={14} /> Status del mes
        </div>
      </div>
      <div className="al-admin-ai-status">
        {items.map(([label, count, color]) => (
          <div key={label} className="al-admin-ai-status-row">
            <span className="al-admin-ai-status-dot" style={{ background: color }} />
            <span style={{ flex: 1 }}>{label}</span>
            <b>{count}</b>
            <span className="tiny muted" style={{ minWidth: 48, textAlign: "right" }}>
              {((count / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
        {s.total_tokens_cache_read > 0 && (
          <div className="tiny muted" style={{ marginTop: 8, padding: "6px 8px", borderRadius: 6, background: "var(--bg)" }}>
            💸 {formatCompact(s.total_tokens_cache_read)} tokens leídos de cache (90% de descuento)
          </div>
        )}
      </div>
    </div>
  );
}

function TopCoachesCard({
  s,
  onPickUser,
}: {
  s: AiUsageSummary;
  onPickUser: (uid: number) => void;
}) {
  return (
    <div className="card al-admin-card">
      <div className="card-header">
        <div className="card-title">
          <Icon name="user" size={14} /> Top 10 del mes
        </div>
      </div>
      {s.top_coaches.length === 0 ? (
        <div className="muted tiny" style={{ padding: 12 }}>
          Sin generaciones este mes todavía.
        </div>
      ) : (
        <table className="al-admin-table al-admin-ai-top">
          <thead>
            <tr>
              <th>Coach</th>
              <th>Plan</th>
              <th style={{ textAlign: "right" }}>Gens</th>
              <th style={{ textAlign: "right" }}>Errores</th>
              <th style={{ textAlign: "right" }}>Costo</th>
            </tr>
          </thead>
          <tbody>
            {s.top_coaches.map((c) => (
              <tr key={c.user_id}>
                <td>
                  <button
                    className="link-btn"
                    onClick={() => onPickUser(c.user_id)}
                    title="Filtrar tabla por este coach"
                  >
                    <b>{c.name || c.email}</b>
                  </button>
                  <div className="tiny muted">{c.email}</div>
                </td>
                <td><span className="chip">{tierLabel(c.plan_tier)}</span></td>
                <td style={{ textAlign: "right" }}>{c.success_count}</td>
                <td style={{ textAlign: "right" }}>
                  {c.error_count > 0 ? (
                    <span style={{ color: "var(--danger)" }}>{c.error_count}</span>
                  ) : (
                    <span className="muted">0</span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>${c.total_cost_usd.toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function GenerationsTable({
  rows,
  onPickUser,
}: {
  rows: AiUsageRow[];
  onPickUser: (uid: number) => void;
}) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }} className="muted">
        Sin generaciones que coincidan con los filtros.
      </div>
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="al-admin-table">
        <thead>
          <tr>
            <th>Cuándo</th>
            <th>Coach</th>
            <th>Status</th>
            <th>Plan</th>
            <th style={{ textAlign: "right" }}>Tokens</th>
            <th style={{ textAlign: "right" }}>Costo</th>
            <th style={{ textAlign: "right" }}>Latencia</th>
            <th>Resumen / error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>
                <div>{relativeTime(r.created_at)}</div>
                <div className="tiny muted">{formatDateTime(r.created_at)}</div>
              </td>
              <td>
                <button
                  className="link-btn"
                  onClick={() => onPickUser(r.user_id)}
                  title="Filtrar por este coach"
                >
                  {r.user_name || r.user_email}
                </button>
                <div className="tiny muted">{r.user_email}</div>
              </td>
              <td><StatusChip status={r.status} /></td>
              <td>{r.plan_id ? `#${r.plan_id}` : <span className="muted">—</span>}</td>
              <td style={{ textAlign: "right" }}>
                <span title={`in:${r.tokens_in ?? 0} · out:${r.tokens_out ?? 0} · cache:${r.tokens_cache_read ?? 0}`}>
                  {formatCompact((r.tokens_in ?? 0) + (r.tokens_out ?? 0))}
                </span>
              </td>
              <td style={{ textAlign: "right" }}>
                {r.cost_usd != null ? `$${r.cost_usd.toFixed(4)}` : <span className="muted">—</span>}
              </td>
              <td style={{ textAlign: "right" }}>
                {r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : <span className="muted">—</span>}
              </td>
              <td style={{ maxWidth: 260 }}>
                {r.status === "success" && r.prompt_summary && (
                  <span className="tiny">{r.prompt_summary}</span>
                )}
                {r.error && (
                  <span className="tiny" style={{ color: "var(--danger)" }} title={r.error}>
                    {r.error.length > 100 ? r.error.slice(0, 100) + "…" : r.error}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    success: { color: "#059669", label: "Success" },
    error: { color: "#dc2626", label: "Error" },
    timeout: { color: "#f59e0b", label: "Timeout" },
    malformed: { color: "#7c3aed", label: "Malformed" },
    quota_exceeded: { color: "#6b7280", label: "Quota" },
  };
  const c = map[status] || { color: "#6b7280", label: status };
  return (
    <span
      className="chip"
      style={{
        background: c.color + "20",
        color: c.color,
        borderColor: c.color + "40",
      }}
    >
      {c.label}
    </span>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="al-admin-mini">
      <div className="al-admin-mini-label">{label}</div>
      <div className="al-admin-mini-value">{value}</div>
    </div>
  );
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

/**
 * AiConfigCard — control para tunear el límite de generaciones IA por cliente
 * por mes (sin redeploy). Default 3. Lo lee/escribe vía /admin/ai-config.
 */
function AiConfigCard() {
  const toast = useToast();
  const [limit, setLimit] = useState<number | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetAiConfig()
      .then((c) => {
        setLimit(c.client_monthly_limit);
        setDraft(String(c.client_monthly_limit));
      })
      .catch(() => setLimit(null));
  }, []);

  const save = async () => {
    const n = Number(draft);
    if (!Number.isInteger(n) || n < 1 || n > 20) {
      toast("Debe ser un número entero entre 1 y 20");
      return;
    }
    setSaving(true);
    try {
      const c = await adminSetAiConfig(n);
      setLimit(c.client_monthly_limit);
      setDraft(String(c.client_monthly_limit));
      toast(`Límite actualizado: ${c.client_monthly_limit} por cliente/mes`);
    } catch (e) {
      toast("No pude guardar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setSaving(false);
    }
  };

  const dirty = limit != null && draft !== String(limit);

  return (
    <div className="card al-admin-card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div className="card-title">
          <Icon name="sparkle" size={15} /> Límite de generación IA por cliente
        </div>
      </div>
      <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span className="muted" style={{ fontSize: 14 }}>
          Máximo de planes que la IA genera por <b>cliente</b> en un mes calendario.
          La IA arma la base; el resto lo ajusta el entrenador.
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
          <input
            type="number"
            min={1}
            max={20}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={limit == null || saving}
            style={{ width: 72, textAlign: "center" }}
          />
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || saving}>
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
